import type { StaffImportReviewRowDetail } from "@lafayette/schemas";
import { useEffect, useRef, useState } from "react";
import { groupHeading, hintText } from "../../../catalog/components/styles";
import { formatDateTime } from "../../../catalog/labels";
import { AsyncState } from "../../../ui/async-state";
import { Button } from "../../../ui/button";
import { Notice } from "../../../ui/notice";
import { StatusMessage } from "../../../ui/status-message";
import type { SaveMessage } from "../../commands";
import { emptyValue, rawText } from "../../components/styles";
import { describeImportError } from "../../errors";
import { describeReviewFailure, type ReviewCommands } from "../commands";
import {
  type DecisionDraft,
  type DecisionKey,
  decisionStates,
  draftOfDecision,
  isReviewable,
  pendingSaves,
  type RowDrafts,
  rowGate,
  sameDraft,
  toDecisionBody,
} from "../decisions";
import { percent, proposalLines, rawCells, ruleLines, targetFieldLabel } from "../format";
import { decisionKindLabels, processingErrorMessages, refusalMessages } from "../labels";
import { useReviewRow } from "../queries";
import { drawerBox, drawerSection, pairList } from "../styles";
import { CandidateList } from "./CandidateList";
import { DecisionForm, decisionFieldId, missingDecisionText } from "./DecisionForm";
import { IssueList, OutcomeBadge } from "./parts";

type RowDrawerProps = Readonly<{
  jobId: string;
  rowId: string;
  /** The job is in review: decisions and the row actions are offered. */
  editable: boolean;
  drafts: RowDrafts;
  onDrafts: (next: RowDrafts) => void;
  commands: ReviewCommands;
  onMessage: (message: SaveMessage | null) => void;
  onClose: () => void;
}>;

/**
 * The detail of one row: what the file holds, what the validator proposes, the issues, the existing items
 * the row may match, the decisions the row owes, and what happened when it was processed. The raw cells
 * and the proposal are shown side by side. Choices stay on the page until they are saved; a 409 keeps
 * them.
 */
export function RowDrawer({
  jobId,
  rowId,
  editable,
  drafts,
  onDrafts,
  commands,
  onMessage,
  onClose,
}: RowDrawerProps) {
  const query = useReviewRow(jobId, rowId);
  const heading = useRef<HTMLHeadingElement>(null);
  const root = useRef<HTMLElement>(null);
  const [attempted, setAttempted] = useState(false);
  const [serverErrors, setServerErrors] = useState<Readonly<Record<DecisionKey, string>>>({});
  const [local, setLocal] = useState<SaveMessage | null>(null);

  const detail: StaffImportReviewRowDetail | undefined = query.data;
  const loaded = detail !== undefined;

  // A new row starts without the messages of the one before.
  // biome-ignore lint/correctness/useExhaustiveDependencies: runs when the row changes.
  useEffect(() => {
    setAttempted(false);
    setServerErrors({});
    setLocal(null);
  }, [rowId]);

  // When the row has loaded, its heading takes the focus, so a screen reader reads where the user is.
  // The heading does not exist while the row loads, so the focus cannot move earlier.
  // biome-ignore lint/correctness/useExhaustiveDependencies: runs when the row changes or has loaded.
  useEffect(() => {
    if (loaded) heading.current?.focus();
  }, [rowId, loaded]);

  if (query.isPending) {
    return (
      <aside className={drawerBox} aria-label="Row detail">
        <AsyncState
          kind="loading"
          title="Loading the row"
          message="Reading the row from the server."
        />
      </aside>
    );
  }
  if (detail === undefined) {
    return (
      <aside className={drawerBox} aria-label="Row detail">
        <AsyncState
          kind="error"
          title="The row could not be loaded"
          message={describeImportError(query.error).message}
          onRetry={() => void query.refetch()}
          accessory={
            <div>
              <Button type="button" variant="secondary" onClick={onClose}>
                Close
              </Button>
            </div>
          }
        />
      </aside>
    );
  }

  const { row } = detail;
  const number = row.sourceRowNumber ?? row.rowNumber;
  const states = decisionStates(detail, drafts);
  const gate = rowGate(detail, drafts);
  const saves = pendingSaves(states);

  const errorsByKey: Record<DecisionKey, string> = { ...serverErrors };
  if (attempted) {
    for (const state of states) {
      if (!state.complete && errorsByKey[state.key] === undefined) {
        errorsByKey[state.key] = missingDecisionText(state);
      }
    }
  }

  const setDraft = (key: DecisionKey, draft: DecisionDraft) => {
    const state = states.find((entry) => entry.key === key);
    const saved = state?.saved ?? null;
    const next = { ...drafts };
    if (saved !== null && sameDraft(draft, draftOfDecision(saved))) delete next[key];
    else next[key] = draft;
    onDrafts(next);
    setServerErrors((current) => {
      if (current[key] === undefined) return current;
      const rest = { ...current };
      delete rest[key];
      return rest;
    });
  };

  const focusFirstProblem = (keys: readonly DecisionKey[]) => {
    const state = states.find((entry) => keys.includes(entry.key));
    if (state !== undefined) document.getElementById(decisionFieldId(state))?.focus();
  };

  async function saveDecisions(): Promise<boolean> {
    setLocal(null);
    onMessage(null);
    const remaining: Record<DecisionKey, DecisionDraft> = { ...drafts };
    for (const state of saves) {
      const version = commands.jobVersion();
      if (version === null) return false;
      const outcome = await commands.decide(rowId, toDecisionBody(version, state));
      if (outcome.ok) {
        delete remaining[state.key];
        onDrafts({ ...remaining });
        continue;
      }
      if (outcome.conflict) {
        setLocal({ tone: "conflict", text: describeReviewFailure(outcome) });
        return false;
      }
      setServerErrors((current) => ({
        ...current,
        [state.key]: "The server did not accept this choice. Check it and try again.",
      }));
      setLocal({ tone: "error", text: outcome.failure.message });
      requestAnimationFrame(() => focusFirstProblem([state.key]));
      return false;
    }
    if (saves.length > 0) setLocal({ tone: "success", text: "The decisions were saved." });
    return true;
  }

  async function review(outcomeName: "APPROVED" | "SKIPPED") {
    if (commands.pending) return;
    if (outcomeName === "APPROVED" && !gate.canApprove) {
      setAttempted(true);
      setLocal(null);
      const open = states.filter((state) => !state.complete).map((state) => state.key);
      requestAnimationFrame(() => focusFirstProblem(open));
      return;
    }
    setLocal(null);
    onMessage(null);
    const version = commands.jobVersion();
    if (version === null) return;
    const outcome = await commands.review({
      expectedJobVersion: version,
      decisions: [
        {
          rowId,
          outcome: outcomeName,
          ...(outcomeName === "APPROVED" && gate.approvalAction !== null
            ? { approvalAction: gate.approvalAction }
            : {}),
        },
      ],
    });
    if (outcome.ok) {
      onMessage({
        tone: "success",
        text:
          outcomeName === "APPROVED" ? `Row ${number} was approved.` : `Row ${number} was skipped.`,
      });
      return;
    }
    if (outcome.conflict) {
      setLocal({ tone: "conflict", text: describeReviewFailure(outcome) });
      return;
    }
    const refusal = outcome.refusals[0];
    setLocal({
      tone: "error",
      text:
        refusal === undefined
          ? outcome.failure.message
          : `The server did not change the row: ${refusalMessages[refusal.code]}.`,
    });
  }

  const reasonsId = "row-approve-reasons";
  // A row that was approved, skipped or imported cannot be reviewed again.
  const settled =
    row.outcome === "APPROVED"
      ? "approved"
      : row.outcome === "SKIPPED"
        ? "skipped"
        : row.outcome === "IMPORTED"
          ? "imported"
          : null;
  const proposal = detail.proposal;
  const acceptedAssists = detail.assists.filter((assist) => assist.accepted);

  return (
    <aside className={drawerBox} aria-labelledby="row-drawer-title" ref={root}>
      <header className="flex flex-wrap items-center gap-2">
        <h2
          id="row-drawer-title"
          tabIndex={-1}
          ref={heading}
          className="m-0 mr-auto text-xl leading-7 font-semibold tracking-tight"
        >
          Row {number}
        </h2>
        <OutcomeBadge outcome={row.outcome} />
        <Button type="button" variant="secondary" onClick={onClose}>
          Close row {number}
        </Button>
      </header>

      <div className="grid gap-4 min-[721px]:grid-cols-2 [&>*]:min-w-0">
        <section aria-labelledby="drawer-raw-title" className={drawerSection}>
          <h3 id="drawer-raw-title" className={groupHeading}>
            From the file
          </h3>
          <dl className={pairList}>
            {rawCells(detail).map((cell) => (
              <div key={cell.label}>
                <dt>{cell.label}</dt>
                <dd className={rawText}>
                  {cell.value === "" ? <span className={emptyValue}>(empty)</span> : cell.value}
                </dd>
              </div>
            ))}
          </dl>
        </section>
        <section aria-labelledby="drawer-proposed-title" className={drawerSection}>
          <h3 id="drawer-proposed-title" className={groupHeading}>
            Proposed
          </h3>
          {proposal === null ? (
            <p className={emptyValue}>There is no proposal for this row.</p>
          ) : (
            <>
              <dl className={pairList}>
                {proposalLines(proposal).map((line) => (
                  <div key={line.key}>
                    <dt>{line.label}</dt>
                    <dd>
                      {line.values.length === 1 ? (
                        <strong className={rawText}>{line.values[0]}</strong>
                      ) : (
                        <ol className="m-0 grid list-none gap-1 p-0">
                          {line.values.map((value, index) => (
                            // biome-ignore lint/suspicious/noArrayIndexKey: values may repeat.
                            <li key={index}>
                              <strong className={rawText}>{value}</strong>
                            </li>
                          ))}
                        </ol>
                      )}
                      {line.note === null ? null : (
                        <span className={`${hintText} block`}>{line.note}</span>
                      )}
                    </dd>
                  </div>
                ))}
              </dl>
              {ruleLines(proposal).length === 0 ? null : (
                <details>
                  <summary className="inline-flex min-h-11 cursor-pointer items-center text-sm font-semibold compact:min-h-8">
                    How the values were made
                  </summary>
                  <ul className="m-0 grid gap-1 pl-5">
                    {ruleLines(proposal).map((entry) => (
                      <li key={entry.key}>
                        <strong>{entry.label}</strong>: {entry.text}
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </>
          )}
        </section>
      </div>

      <section aria-labelledby="drawer-issues-title" className={drawerSection}>
        <h3 id="drawer-issues-title" className={groupHeading}>
          Issues
        </h3>
        <IssueList issues={row.issues} limit={60} />
      </section>

      {acceptedAssists.length === 0 ? null : (
        <section aria-labelledby="drawer-assist-title" className={drawerSection}>
          <h3 id="drawer-assist-title" className={groupHeading}>
            Values suggested by a model
          </h3>
          <ul className="m-0 grid gap-1 pl-5">
            {acceptedAssists.map((assist) => (
              <li key={assist.id}>
                <span aria-hidden="true">✓ </span>
                {targetFieldLabel(assist.fieldCode)}: the model was sure enough (
                {percent(assist.confidenceBp)}) for its answer to be used without your decision.
                Model {assist.modelId}, version {assist.modelVersion}, prompt {assist.promptVersion}
                .
              </li>
            ))}
          </ul>
        </section>
      )}

      <CandidateList candidates={detail.candidates} imported={row.outcome === "IMPORTED"} />

      {editable ? (
        <section aria-labelledby="drawer-decisions-title" className={drawerSection}>
          <h3 id="drawer-decisions-title" className={groupHeading}>
            Decisions
          </h3>
          {states.length === 0 ? (
            <p>This row needs no decision.</p>
          ) : (
            states.map((state) => (
              <DecisionForm
                key={state.key}
                state={state}
                detail={detail}
                drafts={drafts}
                error={errorsByKey[state.key]}
                disabled={commands.pending || !isReviewable(row.outcome)}
                onChange={(draft) => setDraft(state.key, draft)}
              />
            ))
          )}
          {saves.length > 0 ? (
            <div>
              <Button
                type="button"
                disabled={commands.pending}
                onClick={() => void saveDecisions()}
              >
                {commands.pending ? "Working…" : "Save decisions"}
              </Button>
            </div>
          ) : null}
        </section>
      ) : detail.decisions.length === 0 ? null : (
        <section aria-labelledby="drawer-decisions-title" className={drawerSection}>
          <h3 id="drawer-decisions-title" className={groupHeading}>
            Decisions
          </h3>
          <ul className="m-0 grid gap-1 pl-5">
            {detail.decisions.map((decision) => (
              <li key={decision.id}>
                {decisionKindLabels[decision.kind]}, saved {formatDateTime(decision.createdAt)}
                {decision.decidedByName === null ? "" : ` by ${decision.decidedByName}`}.
              </li>
            ))}
          </ul>
        </section>
      )}

      {detail.attempts.length === 0 ? null : (
        <section aria-labelledby="drawer-attempts-title" className={drawerSection}>
          <h3 id="drawer-attempts-title" className={groupHeading}>
            Processing
          </h3>
          <ol className="m-0 grid gap-1 pl-5">
            {detail.attempts.map((attempt) => (
              <li key={attempt.attemptNumber}>
                Attempt {attempt.attemptNumber}:{" "}
                {attempt.result === "SUCCEEDED"
                  ? "succeeded"
                  : attempt.result === "FAILED_RETRYABLE"
                    ? "failed, will be tried again"
                    : "failed"}
                {attempt.errorCode === null
                  ? ""
                  : `. ${processingErrorMessages[attempt.errorCode]}`}
              </li>
            ))}
          </ol>
        </section>
      )}

      {editable && settled ? (
        <Notice tone="info" role="status">
          This row is {settled}. It cannot be changed here.
        </Notice>
      ) : null}
      {editable && !settled ? (
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            aria-disabled={gate.canApprove && !commands.pending ? undefined : "true"}
            aria-describedby={gate.reasons.length > 0 ? reasonsId : undefined}
            onClick={() => void review("APPROVED")}
          >
            Approve row
          </Button>
          <Button
            type="button"
            variant="secondary"
            disabled={!gate.canSkip || commands.pending}
            onClick={() => void review("SKIPPED")}
          >
            Skip row
          </Button>
          {gate.reasons.length > 0 ? (
            <ul
              id={reasonsId}
              className="m-0 grid basis-full list-none gap-1 p-0"
              aria-live={attempted ? "assertive" : "polite"}
            >
              {gate.reasons.map((reason) => (
                <li key={reason}>
                  <span aria-hidden="true">⚠ </span>
                  {reason}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
      <StatusMessage message={local} />
    </aside>
  );
}
